/* eslint-disable */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  CardTitle,
  CardText,
  Row,
  Col,
  Input,
  FormGroup,
  Label,
  Alert,
  Spinner,
  Badge
} from 'reactstrap';
import Switch from 'react-switch';
import { toast } from 'react-toastify';
import BZAPIService from '../../services/BZAPIService';
import servicenow from '../../constants/servicenow';
import ProductPriority from './section/ProductPriority';
import AWSFilters from './section/AWSFilters';
import { productDisplayName } from '../bz/productDisplayName';

export default function BulkUpdateWizard({
  isOpen,
  toggle,
  selectedProductIds,
  selectedProducts,
  vendorId,
  vendor,
  onSuccess
}) {
  const snPriorityOptions = servicenow.SN_PRIORITY_OPTIONS;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [resetLastExecution, setResetLastExecution] = useState(false);

  const [vendorStatuses, setVendorStatuses] = useState([]);
  const [vendorBugTypes, setVendorBugTypes] = useState([]);
  const [vendorBugCategories, setVendorBugCategories] = useState([]);
  const [vendorBugServices, setVendorBugServices] = useState([]);
  const [vendorProductBugCategories, setVendorProductBugCategories] = useState([]);
  const [vendorResolutions, setVendorResolutions] = useState([]);

  const [priorityMaps, setPriorityMaps] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [includedComponents, setIncludedComponents] = useState([]);
  const [excludedComponents, setExcludedComponents] = useState([]);
  const [searchFields, setSearchFields] = useState([]);
  const [vendorEventScopes, setVendorEventScopes] = useState([]);
  const [vendorImpactedRegions, setVendorImpactedRegions] = useState([]);
  const [vendorMitigationTypes, setVendorMitigationTypes] = useState([]);
  const [vendorRiskCategories, setVendorRiskCategories] = useState([]);

  const [loader, setLoader] = useState(false);
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState('0');
  const [limit, setLimit] = useState('2500');
  const [updateErrors, setUpdateErrors] = useState([]);
  const [enableChatGptPriority, setEnableChatGptPriority] = useState(false);

  useEffect(() => {
    async function loadComponents() {
      // No Cisco: it stopped returning a bug's component in September 2025 (STRY0038862).
      if (vendorId === 'rh' || vendorId === 'mongodb') {
        try {
          const components = await BZAPIService.getComponents(vendorId, query, offset, limit);
          const includedComponentsList = components.results || [];
          setIncludedComponents(includedComponentsList);
        } catch (e) {
          console.error('Failed to load components', e);
        }
      } else {
        // The wizard stays mounted across vendors; don't show the previous vendor's components.
        setIncludedComponents([]);
      }
    }

    async function loadAiSettings() {
      try {
        const aiSettingsRes = await BZAPIService.getVendorAiSetting(vendorId);
        const isAiEnabled = !!(aiSettingsRes?.vendor?.aiEnabled && aiSettingsRes?.globalAi?.aiEnabled);
        setEnableChatGptPriority(isAiEnabled);
      } catch (e) {
        // AI settings are optional for non-AI vendors; fall back to legacy mode
        setEnableChatGptPriority(false);
      }
    }

    if (isOpen && vendor) {
      loadAiSettings();
      loadComponents();
      const fields = vendor?.vendorData?.searchFields || [];
      setSearchFields(fields);
    }
  }, [isOpen, vendor, vendorId, query, offset, limit]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  function resetForm() {
    setIsSubmitted(false);
    setIsDisabled(false);
    setResetLastExecution(false);
    setVendorStatuses([]);
    setVendorBugTypes([]);
    setVendorBugCategories([]);
    setVendorBugServices([]);
    setVendorProductBugCategories([]);
    setVendorResolutions([]);
    setPriorityMaps([]);
    setKeywords([]);
    setExcludedComponents([]);
    setVendorEventScopes([]);
    setVendorImpactedRegions([]);
    setVendorMitigationTypes([]);
    setVendorRiskCategories([]);
    setUpdateErrors([]);
  }

  function handleToggleEnable() {
    setIsDisabled(!isDisabled);
  }

  function validateForm() {
    setIsSubmitted(true);

    if (vendor?.vendorData?.statuses?.length && !vendorStatuses.length) {
      toast.error('Please select bug statuses');
      return false;
    }

    const vendorBugCategoryOptions = vendor?.vendorData?.bugCategories || [];
    if (vendorBugCategoryOptions.length && !vendorBugCategories.length) {
      toast.error('Please select bug categories');
      return false;
    }

    if (vendor?.vendorData?.eventScopes?.length && !vendorEventScopes.length) {
      toast.error('Please select event scopes');
      return false;
    }

    if (vendor?.vendorData?.vendorImpactedRegions?.length && !vendorImpactedRegions.length) {
      toast.error('Please select impacted regions');
      return false;
    }

    if (vendor?.vendorData?.vendorBugServices?.length && !vendorBugServices.length) {
      toast.error('Please select bug services');
      return false;
    }

    const bugCategoryOptions = vendor?.vendorData?.vendorProductBugCategories?.['BIG-IP'] || [];
    if (bugCategoryOptions?.length && !vendorProductBugCategories.length) {
      toast.error('Please select product bug categories');
      return false;
    }

    let validPriorities = true;
    priorityMaps.forEach(item => {
      if (!item.vendorPriority || !item.snPriority) {
        validPriorities = false;
      }
    });

    if (!validPriorities || priorityMaps.length === 0) {
      toast.error('Please configure priority mappings');
      return false;
    }

    return true;
  }

  function buildPayload() {
    const vendorPriorities = priorityMaps?.map(mapping => {
      return {
        value: mapping.vendorPriority.value,
        label: mapping.vendorPriority.value,
        snPriority: mapping.snPriority.value
      };
    });

    const vendorData = {
      vendorBugResolutions: vendorResolutions,
      vendorBugCategories,
      vendorBugServices,
      vendorBugTypes,
      excludedBugComponents: excludedComponents,
      excludedKws: keywords,
      searchFields: searchFields,
      vendorBugModules: vendorProductBugCategories,
      vendorEventScopes,
      vendorImpactedRegions,
      vendorRiskCategories,
      vendorMitigationTypes
    };

    const payload = {
      managedProductIds: selectedProductIds,
      vendorId: vendorId,
      vendorStatuses,
      vendorPriorities,
      vendorData,
      isDisabled,
      resetLastExecution
    };

    return payload;
  }

  async function handleSubmit() {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = buildPayload();
      const response = await BZAPIService.bulkUpdateManagedProducts(payload);
      const { results } = response;

      if (!results || results.length === 0) {
        toast.error('No products were updated');
        return;
      }

      const successCount = results.length;
      const expectedCount = selectedProductIds.length;

      if (successCount === expectedCount) {
        toast.success(`Successfully updated ${successCount} product(s)`);
        resetForm();
        toggle();
        onSuccess();
      } else {
        const updatedIds = results.map(r => r.id);
        const failedIds = selectedProductIds.filter(id => !updatedIds.includes(id));
        setUpdateErrors(
          failedIds.map(id => ({
            id,
            error: 'Update failed'
          }))
        );
        toast.warning(`Updated ${successCount} of ${expectedCount} product(s). Some updates failed.`);
      }
    } catch (error) {
      console.error('Bulk update error:', error);
      toast.error('Failed to update products: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleCancel() {
    resetForm();
    toggle();
  }

  const vendorBugTypeOptions = vendor?.vendorData?.bugTypes || [];
  const vendorBugCategoryOptions = vendor?.vendorData?.bugCategories || [];

  return (
    <Modal isOpen={isOpen} toggle={handleCancel} size="xl" style={{ maxWidth: '90%' }}>
      <ModalHeader toggle={handleCancel}>Bulk Update Managed Products ({selectedProductIds.length} selected)</ModalHeader>
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {!vendor ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">Loading vendor configuration...</p>
          </div>
        ) : (
          <div>
            {updateErrors.length > 0 && (
              <Alert color="danger" fade={false}>
                <h6>Failed Updates:</h6>
                <ul>
                  {updateErrors.map(err => (
                    <li key={err.id}>
                      Product ID {err.id}: {err.error}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            <Card className="mb-4 bg-light">
              <CardBody>
                <h6 className="mb-3">Selected Products ({selectedProducts.length}):</h6>
                <div className="d-flex flex-wrap gap-2">
                  {selectedProducts.map(product => (
                    <Badge key={product.id} color="primary" pill className="px-3 py-2">
                      {productDisplayName(vendorId, product.name)}
                    </Badge>
                  ))}
                </div>
              </CardBody>
            </Card>

            <Card className="mb-4">
              <CardBody>
                <Row className="justify-content-between align-items-center">
                  <Col md>
                    <h5 className="mb-2 mb-md-0">Product Status</h5>
                  </Col>
                  <Col xs="auto">
                    <h5>
                      <span style={{ position: 'relative', top: '3px' }}>
                        <Switch onChange={handleToggleEnable} checked={!isDisabled} height={20} onColor="#253746" />{' '}
                      </span>
                      <span>{isDisabled ? 'Disabled' : 'Enabled'}</span>
                    </h5>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card className="mb-4">
              <CardBody>
                <FormGroup check>
                  <Input
                    type="checkbox"
                    id="resetLastExecution"
                    checked={resetLastExecution}
                    onChange={e => setResetLastExecution(e.target.checked)}
                  />
                  <Label for="resetLastExecution" check>
                    <strong>Reset Last Execution</strong> - Force full reprocessing on next bug service run
                  </Label>
                </FormGroup>
              </CardBody>
            </Card>

            {vendorStatuses?.length && vendorStatuses[0]?.hasOwnProperty('variables') ? (
              <Card bg={'light'} text={'dark'} className="mb-4">
                <CardBody>
                  <CardTitle className="">Select Bug Statuses to Monitor</CardTitle>
                  <CardText>Select multiple entries if necessary</CardText>
                  <Row>
                    {vendorStatuses.map(option => {
                      return (
                        <Col lg="6" key={option.label + 'option'}>
                          <Card className="mt-2">
                            <CardBody>
                              <Row>
                                <Col xs={8}>
                                  <CardTitle>{option.label}</CardTitle>
                                </Col>
                                <Col xs={4} className="text-right">
                                  <Switch
                                    id={option.label + '-switch'}
                                    onChange={() => {
                                      const newStatuses = [...vendorStatuses];
                                      const index = newStatuses.findIndex(s => s.label === option.label);
                                      newStatuses[index].active = newStatuses[index].active === 1 ? 0 : 1;
                                      setVendorStatuses(newStatuses);
                                    }}
                                    checked={option.active === 1}
                                    uncheckedIcon={false}
                                    onColor="#253746"
                                    height={14}
                                    width={28}
                                  />
                                </Col>
                              </Row>
                              {option.variables.map(variable => {
                                return (
                                  <Row key={variable.label + option.label + 'variableKey'} className="mt-2">
                                    <Col>
                                      <strong>{variable.label}</strong>
                                    </Col>
                                    <Col>
                                      <Input
                                        placeholder="0"
                                        type="number"
                                        value={variable.value}
                                        onChange={e => {
                                          const newStatuses = [...vendorStatuses];
                                          const statusIndex = newStatuses.findIndex(s => s.label === option.label);
                                          const varIndex = newStatuses[statusIndex].variables.findIndex(
                                            v => v.label === variable.label
                                          );
                                          newStatuses[statusIndex].variables[varIndex].value = e.target.value;
                                          setVendorStatuses(newStatuses);
                                        }}
                                        disabled={option.active !== 1}
                                        min="0"
                                      />
                                    </Col>
                                  </Row>
                                );
                              })}
                            </CardBody>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                </CardBody>
              </Card>
            ) : null}

            {vendorId === 'aws' || vendorId === 'azure' ? (
              <AWSFilters
                vendorEventScopeOptions={vendor?.vendorData?.eventScopes}
                setVendorEventScopes={setVendorEventScopes}
                isSubmitted={isSubmitted}
                vendorEventScopes={vendorEventScopes}
                vendorLabel={vendor?.name}
              />
            ) : null}

<ProductPriority
              title="Bug Configuration"
              vendorId={vendorId}
              vendorPriorityOptions={vendor?.vendorData?.priorities}
              enableChatGptPriority={enableChatGptPriority}
              vendorResolutionOptions={vendor?.vendorData?.vendorBugResolutions}
              vendorStatusOptions={vendor?.vendorData?.statuses}
              setVendorStatuses={setVendorStatuses}
              vendorStatuses={vendorStatuses}
              setVendorBugTypes={setVendorBugTypes}
              setVendorBugCategories={setVendorBugCategories}
              vendorBugTypeOptions={vendorBugTypeOptions}
              vendorBugCategoryOptions={vendorBugCategoryOptions}
              vendorBugTypes={vendorBugTypes}
              vendorBugCategories={vendorBugCategories}
              setVendorResolutions={setVendorResolutions}
              vendorResolutions={vendorResolutions}
              snProductId={null}
              isSubmitted={isSubmitted}
              priorityMaps={priorityMaps}
              setPriorityMaps={setPriorityMaps}
              snPriorityOptions={snPriorityOptions}
              keywords={keywords}
              setKeywords={setKeywords}
              includedComponents={includedComponents}
              setIncludedComponents={setIncludedComponents}
              excludedComponents={excludedComponents}
              setExcludedComponents={setExcludedComponents}
              searchFields={searchFields}
              setSearchFields={setSearchFields}
              loader={loader}
              setLoader={setLoader}
              setQuery={setQuery}
              query={query}
              offset={offset}
              limit={limit}
              setOffset={setOffset}
              setLimit={setLimit}
              setVendorBugServices={setVendorBugServices}
              vendorBugServices={vendorBugServices}
              vendorBugServiceOptions={vendor?.vendorData?.vendorBugServices}
              vendorProductBugCategories={vendorProductBugCategories}
              vendorProductBugCategoryOptions={
                vendor?.vendorData?.vendorProductBugCategories && vendor?.vendorData?.vendorProductBugCategories['BIG-IP']
                  ? vendor?.vendorData?.vendorProductBugCategories['BIG-IP']
                  : []
              }
              setVendorProductBugCategories={setVendorProductBugCategories}
              vendorImpactedRegions={vendorImpactedRegions}
              setVendorImpactedRegions={setVendorImpactedRegions}
              vendorImpactedRegionOptions={vendor?.vendorData?.vendorImpactedRegions}
              vendorRiskCategoryOptions={vendor?.vendorData?.vendorRiskCategories}
              vendorRiskCategories={vendorRiskCategories}
              setVendorRiskCategories={setVendorRiskCategories}
              vendorMitigationTypes={vendorMitigationTypes}
              setVendorMitigationTypes={setVendorMitigationTypes}
              vendorMitigationTypeOptions={vendor?.vendorData?.vendorMitigationTypes}
            />
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={isSubmitting || !vendor}>
          {isSubmitting ? (
            <div>
              <Spinner size="sm" className="mr-2" />
              Updating {selectedProductIds.length} product(s)...
            </div>
          ) : (
            `Update ${selectedProductIds.length} Product(s)`
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

BulkUpdateWizard.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  selectedProductIds: PropTypes.array.isRequired,
  selectedProducts: PropTypes.array.isRequired,
  vendorId: PropTypes.string.isRequired,
  vendor: PropTypes.object,
  onSuccess: PropTypes.func.isRequired
};
